import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Image } from "primereact/image";
import { useTranslation } from "react-i18next";

const MyChartComponent = ({
  chartData = [],
  measuresMetadata = [],
  visible,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [option, setOption] = useState(null);

  // ───────────────────────────────
  // 🧩 Helpers
  // ───────────────────────────────
  const getMeasureColor = (measureName) => {
    const mes = measureName.includes("-")
      ? measureName.split("-")[1]
      : measureName;
    const measure = measuresMetadata.find((m) => m.Name === mes);
    if (measure?.Color) {
      if (typeof measure.Color === "number") {
        return `#${measure.Color.toString(16).padStart(6, "0")}`;
      }
      return measure.Color.startsWith("#")
        ? measure.Color
        : `#${measure.Color}`;
    }
    return `#${Math.floor(Math.random() * 0xffffff)
      .toString(16)
      .padStart(6, "0")}`;
  };

  // ───────────────────────────────
  // 🔄 Normalize chartData by TS
  // ───────────────────────────────
  const normalizeByTS = (data) => {
    const map = {};

    data.forEach((row) => {
      const { TS, ...rest } = row;
      if (!map[TS]) map[TS] = { TS };
      Object.assign(map[TS], rest);
    });

    return Object.values(map).sort((a, b) => new Date(a.TS) - new Date(b.TS));
  };

  // ───────────────────────────────
  // 📊 Build option async
  // ───────────────────────────────
  const buildOptionAsync = async (data, onProgress) => {
    return new Promise((resolve) => {
      const normalizedData = normalizeByTS(data);
      const labels = normalizedData.map((d) => d.TS);

      // Get all numeric keys excluding TS and ID
      const keys = Array.from(
        normalizedData.reduce((set, row) => {
          Object.keys(row).forEach((k) => {
            if (k !== "TS" && k !== "ID" && typeof row[k] === "number") {
              set.add(k);
            }
          });
          return set;
        }, new Set()),
      );

      const series = [];
      let i = 0;

      const processNext = () => {
        const key = keys[i];

        series.push({
          name: key,
          type: "line",
          data: normalizedData.map((row) => row?.[key] ?? null),
          showSymbol: false,
          sampling: "lttb",
          lineStyle: { width: 2 },
          itemStyle: { color: getMeasureColor(key) },
          emphasis: {
            focus: "series",
            lineStyle: { width: 3 },
          },
        });

        i++;
        onProgress(Math.round((i / keys.length) * 100));

        if (i < keys.length) {
          requestIdleCallback(processNext);
        } else {
          resolve({
            backgroundColor: "#ffffff",
            grid: { top: 40, left: 60, right: 30, bottom: 110 },
            tooltip: {
              trigger: "axis",
              axisPointer: {
                type: "cross",
                label: { backgroundColor: "#6a7985" },
              },
              formatter: function (params) {
                if (!params || !params.length) return "";

                const colCount = 2; // numero di colonne desiderate
                let html = `<div style="text-align:center;"><strong>${params[0].axisValue}</strong></div><table style="margin:auto;">`;
                params.forEach((p, idx) => {
                  if (idx % colCount === 0) html += "<tr>"; // nuova riga ogni colCount elementi
                  html += `<td style="padding: 2px 10px; color:${p.color}">${p.seriesName}: ${p.data}</td>`;
                  if ((idx + 1) % colCount === 0) html += "</tr>";
                });
                if (params.length % colCount !== 0) html += "</tr>"; // chiudi ultima riga se incompleta
                html += "</table>";
                return html;
              },
            },

            legend: {
              type: "scroll",
              bottom: 60,
              itemWidth: 12,
              itemHeight: 12,
              textStyle: { color: "#444" },
            },
            toolbox: { right: 20, feature: { restore: {}, saveAsImage: {} } },
            dataZoom: [
              { type: "inside", zoomOnMouseWheel: true, moveOnMouseMove: true },
              {
                type: "slider",
                height: 22,
                bottom: 20,
                fillerColor: "rgba(100,149,237,0.25)",
                handleStyle: { color: "#6495ED" },
              },
            ],
            xAxis: {
              type: "category",
              data: labels,
              boundaryGap: false,
              axisLine: { lineStyle: { color: "#999" } },
              axisLabel: { color: "#666" },
            },
            yAxis: {
              type: "value",
              axisLine: { show: true, lineStyle: { color: "#999" } },
              axisLabel: { color: "#666" },
              splitLine: { lineStyle: { color: "#eee", type: "dashed" } },
            },
            series,
          });
        }
      };

      requestIdleCallback(processNext);
    });
  };

  // ───────────────────────────────
  // ⚙️ Effect
  // ───────────────────────────────
  useEffect(() => {
    if (!visible || !chartData.length || !measuresMetadata.length) return;

    setLoading(true);
    setProgress(0);

    buildOptionAsync(chartData, setProgress).then((opt) => {
      setOption(opt);
      setLoading(false);
    });
  }, [visible, chartData, measuresMetadata]);

  // ───────────────────────────────
  // 🖼️ Render
  // ───────────────────────────────
  if (loading) {
    return (
      <div>
        <h3 className="text-2xl text-gray-500">
          {t("processing")} {progress}% (
          {Math.round((progress / 100) * chartData.length)} / {chartData.length}{" "}
          {t("datapoints")})
        </h3>
        <Image
          src="/pms/loading.gif"
          alt="loading"
          style={{ maxWidth: 200, height: 200 }}
        />
      </div>
    );
  }

  if (!option) {
    return (
      <div className="flex flex-col items-center text-center">
        <h1>{t("noData")}</h1>
        <Image src="/pms/no_data.gif" alt="no_data" style={{ maxWidth: 300 }} />
      </div>
    );
  }

  return (
    <ReactECharts
      option={option}
      style={{ height: "520px", width: "100%" }}
      notMerge
      lazyUpdate
    />
  );
};

export default MyChartComponent;
